<script lang="ts">
  import { Link } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import { acting, auth, currentUser, managedBy, type User } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import Avatar from '../../lib/components/Avatar.svelte';
  import Modal from '../../lib/components/Modal.svelte';
  import { squareJpeg } from '../../lib/utils/photos';
  import { t } from '../../lib/i18n/index.ts';

  // around splits a message at the spots a link, field, or styled word fills, in order.
  const around = (message: string) => message.split('\u0000');
  const sharingNote = around(t('settingsSharingNote', { groups: '\u0000' }));
  const phoneLabel = around(t('settingsPhoneLabel', { optional: '\u0000' }));
  const emailLabel = around(t('settingsEmailLabel', { optional: '\u0000' }));

  let error = $state('');

  // Profile: how the member appears to their groups (spec 010 FR-015, 010 FR-026).
  let photoBusy = $state(false);
  let photoInput: HTMLInputElement | undefined = $state();

  async function reloadMe() {
    auth.setUser(await api.get<User>('/auth/me'));
  }

  async function choosePhoto(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    photoBusy = true;
    try {
      await api.putBlob('/me/photo', await squareJpeg(file));
      await reloadMe();
    } catch (err: any) {
      error = err.message;
    } finally {
      photoBusy = false;
      if (photoInput) photoInput.value = '';
    }
  }

  async function removePhoto() {
    photoBusy = true;
    try {
      await api.delete('/me/photo');
      await reloadMe();
    } catch (err: any) {
      error = err.message;
    } finally {
      photoBusy = false;
    }
  }

  // The name, phone number, email address, and password are the account's
  // details (spec 001 FR-015, 001 FR-007); the phone and email are optional.
  let name = $state('');
  let phone = $state('');
  let email = $state('');
  let nameNote = $state('');
  let loadedDetails = false;
  $effect(() => {
    if ($currentUser && !loadedDetails) {
      loadedDetails = true;
      name = $currentUser.display_name;
      phone = $currentUser.phone ?? '';
      email = $currentUser.email ?? '';
    }
  });

  // saveName saves the name, phone, and email when one is left after a
  // change (010 FR-080).
  async function saveName() {
    nameNote = '';
    try {
      const me = await api.patch<User>('/me', { display_name: name, phone, email });
      auth.setUser(me);
      phone = me.phone ?? '';
      email = me.email ?? '';
      nameNote = t('settingsSaved');
      error = '';
    } catch (err: any) {
      error = err.message;
    }
  }

  // Changing the password happens in a dialog that asks for the current
  // one, or only the new one for a parent acting for a child (010 FR-081).
  let changingPassword = $state(false);
  let currentPassword = $state('');
  let newPassword = $state('');
  let passwordError = $state('');
  let passwordNote = $state('');

  function openPassword() {
    currentPassword = '';
    newPassword = '';
    passwordError = '';
    changingPassword = true;
  }

  async function changePassword(e: SubmitEvent) {
    e.preventDefault();
    try {
      await api.post('/me/password', $acting ? { new_password: newPassword } : { current_password: currentPassword, new_password: newPassword });
      changingPassword = false;
      passwordNote = $acting ? t('settingsPasswordChanged') : t('settingsPasswordChangedPhones');
    } catch (err: any) {
      passwordError = err.message;
    }
  }

  // Deleting the account happens in a dialog that asks for its password,
  // and ends this session (spec 001 FR-024, 010 FR-081).
  let deleting = $state(false);
  let deletePassword = $state('');
  let deleteError = $state('');

  function openDelete() {
    deletePassword = '';
    deleteError = '';
    deleting = true;
  }

  async function deleteAccount(e: SubmitEvent) {
    e.preventDefault();
    try {
      await api.post('/me/delete', { password: deletePassword });
    } catch (err: any) {
      deleteError = err.message;
      return;
    }
    await auth.logout();
  }
</script>

{#if error}
  <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
{/if}

<!-- A child's settings are their parents' to change, so they show read-only (spec 001 FR-098). -->
<fieldset disabled={!!$managedBy} class="min-w-0">
{#if $currentUser}
  {@const me = $currentUser}
  <div class="card p-5 mb-6">
    <PageHeader title={t('settingsProfile')} level="section" />
    <div class="flex items-center gap-4">
      <Avatar name={me.display_name} userId={me.id} color={me.color ?? 'var(--theme-blue)'} photo={me.photo} size={64} />
      <div class="flex-1">
        <p class="font-semibold">{me.display_name}</p>
        <p class="text-sm text-text-muted">{t('settingsProfileNote')}</p>
        <div class="flex gap-2 mt-2">
          <button class="btn btn-secondary btn-sm" disabled={photoBusy} onclick={() => photoInput?.click()}>
            <i aria-hidden="true" class="las la-camera mr-1"></i>{me.photo ? t('settingsChangePhoto') : t('settingsAddPhoto')}
          </button>
          {#if me.photo}
            <button class="btn btn-secondary btn-sm" disabled={photoBusy} onclick={removePhoto}>{t('settingsRemovePhoto')}</button>
          {/if}
          <input bind:this={photoInput} type="file" accept="image/*" class="hidden" onchange={choosePhoto} />
        </div>
      </div>
    </div>
    <div class="flex flex-wrap items-end gap-3 mt-4">
      <label class="text-sm">
        <span class="block mb-1">{t('settingsName')}</span>
        <input class="input w-56" bind:value={name} required maxlength="64" oninput={() => (nameNote = '')} onchange={saveName} />
      </label>
      <label class="text-sm">
        <span class="block mb-1">{phoneLabel[0]}<span class="text-text-muted">{t('settingsOptional')}</span>{phoneLabel[1]}</span>
        <input class="input w-48" type="tel" autocomplete="tel" bind:value={phone} maxlength="32" oninput={() => (nameNote = '')} onchange={saveName} />
      </label>
      <label class="text-sm">
        <span class="block mb-1">{emailLabel[0]}<span class="text-text-muted">{t('settingsOptional')}</span>{emailLabel[1]}</span>
        <input class="input w-64" type="email" autocomplete="email" bind:value={email} maxlength="254" oninput={() => (nameNote = '')} onchange={saveName} />
      </label>
      {#if nameNote}<span class="text-sm text-text-muted" aria-live="polite">{nameNote}</span>{/if}
    </div>
    <p class="text-xs text-text-muted mt-2">{t('settingsContactNote')}</p>
    <div class="flex flex-wrap items-center gap-3 mt-4">
      <button type="button" class="btn btn-secondary btn-sm" onclick={openPassword}><i aria-hidden="true" class="las la-key mr-1"></i>{t('settingsChangePassword')}</button>
      {#if passwordNote}<span class="text-sm text-text-muted" aria-live="polite">{passwordNote}</span>{/if}
    </div>
    <p class="text-xs text-text-muted mt-4">{sharingNote[0]}<Link to="/groups">{t('settingsGroupsLink')}</Link>{sharingNote[1]}</p>
  </div>
{/if}

</fieldset>
{#if !$acting && !$managedBy}
  <div class="card p-5 mb-6">
    <PageHeader title={t('settingsDeleteAccount')} level="section" />
    <p class="text-sm text-text-muted mb-3">
      {t('settingsDeleteAccountAbout')}
    </p>
    <button type="button" class="btn btn-danger btn-sm" onclick={openDelete}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteAccount')}</button>
  </div>
{/if}

{#if changingPassword}
  <Modal title={t('settingsChangePassword')} onclose={() => (changingPassword = false)}>
    <form class="flex flex-col gap-3" onsubmit={changePassword}>
      {#if !$acting}
        <label class="text-sm">
          <span class="block mb-1">{t('settingsCurrentPassword')}</span>
          <input class="input w-full" type="password" autocomplete="current-password" bind:value={currentPassword} required oninput={() => (passwordError = '')} />
        </label>
      {/if}
      <label class="text-sm">
        <span class="block mb-1">{t('settingsNewPassword')}</span>
        <input class="input w-full" type="password" autocomplete="new-password" minlength="8" bind:value={newPassword} required oninput={() => (passwordError = '')} />
      </label>
      {#if passwordError}<p class="text-sm text-error" role="alert">{passwordError}</p>{/if}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (changingPassword = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary">{t('settingsChangePassword')}</button>
      </div>
    </form>
  </Modal>
{/if}

{#if deleting}
  <Modal title={t('settingsDeleteAccount')} onclose={() => (deleting = false)}>
    <form class="flex flex-col gap-3" onsubmit={deleteAccount}>
      <p class="text-sm">{t('settingsDeleteAccountConfirm')}</p>
      <label class="text-sm">
        <span class="block mb-1">{t('settingsPassword')}</span>
        <input class="input w-full" type="password" autocomplete="current-password" bind:value={deletePassword} required oninput={() => (deleteError = '')} />
      </label>
      {#if deleteError}<p class="text-sm text-error" role="alert">{deleteError}</p>{/if}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (deleting = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-danger"><i aria-hidden="true" class="las la-trash mr-1"></i>{t('settingsDeleteAccount')}</button>
      </div>
    </form>
  </Modal>
{/if}
